import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageCircle, X, Home, HelpCircle, Bell, MessageSquarePlus } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useChatWidget } from "./useChatWidget";
import HomeTab from "./HomeTab";
import ChatTab from "./ChatTab";
import HelpTab from "./HelpTab";
import ChangelogTab from "./ChangelogTab";
import FeedbackTab from "./FeedbackTab";

interface ChatMessage {
  id: string;
  company_id: string;
  user_id: string | null;
  user_name: string;
  email: string | null;
  content: string;
  is_admin: boolean;
  created_at: string;
  image_url?: string;
}

interface ChatWidgetProps {
  companyId: string;
  companyName: string;
  userName?: string;
  userId?: string;
  embedded?: boolean;
  onClose?: () => void;
  primaryColor?: string;
  textColor?: string;
  userEmail?: string;
}

export const ChatWidget = ({
  companyId,
  companyName,
  userName,
  userId,
  embedded = false,
  onClose,
  primaryColor,
  textColor,
  userEmail
}: ChatWidgetProps) => {
  const {
    isOpen,
    setIsOpen,
    message,
    setMessage,
    messages,
    isLoading,
    visitorName,
    setVisitorName,
    visitorEmail,
    setVisitorEmail,
    nameSubmitted,
    setNameSubmitted,
    activeTab,
    isProcessingAI,
    aiSupportEnabled,
    isUploading,
    selectedImage,
    setSelectedImage,
    changelogs,
    isLoadingChangelogs,
    fileInputRef,
    messagesEndRef,
    handleSubmitName,
    sendMessage,
    handleImageSelect,
    handleFileChange,
    removeSelectedImage,
    formatTime,
    handleTabChange,
    navigateToTab,
    adjustColor,
    getGradientStyle
  } = useChatWidget({
    companyId,
    companyName,
    userName,
    userId,
    embedded,
    userEmail
  });

  // Track if we should show the name/email form
  const [showNameForm, setShowNameForm] = useState(false);

  // Handle tab change with special behavior for chat tab
  const handleTabWithNameCheck = (value: string) => {
    // If trying to access chat tab and name not submitted, show the name form
    if (value === "chat" && !nameSubmitted) {
      setShowNameForm(true);
      return;
    }
    
    // Otherwise, proceed with normal tab change
    setShowNameForm(false);
    handleTabChange(value);
  }

  // Custom submit handler that also hides the name form
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmitName(e);
    // Hide the name form after successful submission
    if (visitorName.trim() && visitorEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(visitorEmail)) {
        setShowNameForm(false);
      }
    }
  };

  return (
    <div className={`${embedded ? "h-full" : "fixed bottom-4 right-4 z-50 flex flex-col items-end"}`}>
      {(primaryColor || textColor) && (
        <style dangerouslySetInnerHTML={{
          __html: `
            .chat-widget-custom-theme .bg-primary {
              background-color: ${primaryColor} !important;
            }
            .chat-widget-custom-theme .text-primary-foreground {
              color: ${textColor} !important;
            }
            .chat-widget-custom-theme .border-primary {
              border-color: ${primaryColor} !important;
            }
            .gradient-header {
              background: linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%);
            }
            .custom-hover-button:hover {
              background-color: ${primaryColor} !important;
              color: ${textColor || '#ffffff'} !important;
            }
          `
        }} />
      )}

      {isOpen || embedded ? (
        <Card className={`${embedded ? "h-full w-full" : "w-[90vw] md:w-[440px] shadow-2xl mb-2 flex flex-col max-h-[85vh] md:max-h-[780px]"} ${primaryColor ? "chat-widget-custom-theme" : ""} overflow-hidden border-0 rounded-2xl`}>
          <CardHeader
            className={`p-5 pb-4 border-b-0 flex-shrink-0 ${primaryColor ? 'gradient-header' : 'bg-primary'}`}
            style={primaryColor ? {
              ...getGradientStyle(),
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%)`
            } : {
              background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 100%)'
            }}
          >
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-lg font-semibold text-white mb-1.5">
                  Hello there 👋
                </CardTitle>
                <p className="text-sm text-white/90 leading-relaxed">
                  How can we help you today?
                </p>
              </div>
              {!embedded ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-white/20 transition-colors flex-shrink-0"
                  onClick={() => setIsOpen(false)}
                  style={{
                    color: textColor || 'white'
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : onClose ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 hover:bg-white/20 transition-colors flex-shrink-0"
                  onClick={onClose}
                  style={{
                    color: textColor || 'white'
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </CardHeader>

          {showNameForm ? (
            <CardContent className="flex-1 p-6 flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/10 overflow-y-auto">
              <form onSubmit={handleNameSubmit} className="w-full max-w-sm space-y-5">
                <div className="space-y-4">
                  <h3 className="text-center font-semibold text-xl">
                    Start a conversation
                  </h3>
                  <p className="text-center text-sm text-muted-foreground">
                    We typically reply in a few minutes
                  </p>
                  <div className="space-y-3 pt-2">
                    <input
                      value={visitorName}
                      onChange={e => setVisitorName(e.target.value)}
                      placeholder="Your name"
                      className="w-full px-4 py-3.5 border-2 border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all bg-background text-base"
                      autoFocus
                    />
                    <input
                      type="email"
                      value={visitorEmail}
                      onChange={e => setVisitorEmail(e.target.value)}
                      placeholder="Your email"
                      className="w-full px-4 py-3.5 border-2 border-border rounded-xl focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all bg-background text-base"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full py-3.5 rounded-xl font-medium shadow-sm hover:shadow-md transition-all"
                  style={primaryColor ? {
                    backgroundColor: primaryColor,
                    color: textColor
                  } : {}}
                >
                  Start Chat
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full py-3 rounded-xl hover:bg-muted"
                  onClick={() => setShowNameForm(false)}
                >
                  Go Back
                </Button>
              </form>
            </CardContent>
          ) : (
            <Tabs value={activeTab} onValueChange={handleTabWithNameCheck} className="flex-1 flex flex-col min-h-0">
              <TabsList className="w-full grid grid-cols-5 bg-muted/30 border-b flex-shrink-0 h-auto p-0">
                <TabsTrigger
                  value="home"
                  className="py-3.5 px-2 flex flex-col items-center gap-1.5 data-[state=active]:text-primary data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary transition-all"
                >
                  <Home className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[11px] font-medium">Home</span>
                </TabsTrigger>
                <TabsTrigger
                  value="chat"
                  className="py-3.5 px-2 flex flex-col items-center gap-1.5 data-[state=active]:text-primary data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary transition-all"
                >
                  <MessageCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[11px] font-medium">Chat</span>
                </TabsTrigger>
                <TabsTrigger
                  value="feedback"
                  className="py-3.5 px-2 flex flex-col items-center gap-1.5 data-[state=active]:text-primary data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary transition-all"
                >
                  <MessageSquarePlus className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[11px] font-medium">Ideas</span>
                </TabsTrigger>
                <TabsTrigger
                  value="help"
                  className="py-3.5 px-2 flex flex-col items-center gap-1.5 data-[state=active]:text-primary data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary transition-all"
                >
                  <HelpCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[11px] font-medium">Help</span>
                </TabsTrigger>
                <TabsTrigger
                  value="changelog"
                  className="py-3.5 px-2 flex flex-col items-center gap-1.5 data-[state=active]:text-primary data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary transition-all"
                >
                  <Bell className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[11px] font-medium">News</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="home" className="flex-1 overflow-y-auto p-5 m-0 min-h-0">
                <HomeTab
                  navigateToTab={navigateToTab}
                  changelogs={changelogs}
                  isLoadingChangelogs={isLoadingChangelogs}
                  primaryColor={primaryColor}
                  textColor={textColor}
                />
              </TabsContent>

              <TabsContent value="chat" className="p-0 m-0 flex flex-col flex-1 overflow-hidden min-h-0">
                <ChatTab 
                  visitorName={visitorName}
                  visitorEmail={visitorEmail}
                  nameSubmitted={nameSubmitted}
                  setVisitorName={setVisitorName}
                  setVisitorEmail={setVisitorEmail}
                  setNameSubmitted={setNameSubmitted}
                  messages={messages}
                  isLoading={isLoading}
                  isProcessingAI={isProcessingAI}
                  message={message}
                  setMessage={setMessage}
                  selectedImage={selectedImage}
                  setSelectedImage={setSelectedImage}
                  isUploading={isUploading}
                  sendMessage={sendMessage}
                  handleSubmitName={handleSubmitName}
                  handleImageSelect={handleImageSelect}
                  removeSelectedImage={removeSelectedImage}
                  formatTime={formatTime}
                  fileInputRef={fileInputRef}
                  messagesEndRef={messagesEndRef}
                  companyName={companyName}
                  aiSupportEnabled={aiSupportEnabled}
                  primaryColor={primaryColor}
                  textColor={textColor}
                />
              </TabsContent>

              <TabsContent value="feedback" className="m-0 p-0 flex-1 overflow-y-auto min-h-0">
                <FeedbackTab
                  companyId={companyId}
                  visitorName={visitorName}
                  visitorEmail={visitorEmail}
                  primaryColor={primaryColor}
                  textColor={textColor}
                />
              </TabsContent>

              <TabsContent value="help" className="m-0 p-0 flex-1 overflow-y-auto min-h-0">
                <HelpTab companyId={companyId} />
              </TabsContent>

              <TabsContent value="changelog" className="m-0 p-0 flex-1 overflow-y-auto min-h-0">
                <ChangelogTab
                  changelogs={changelogs}
                  isLoadingChangelogs={isLoadingChangelogs}
                />
              </TabsContent>
            </Tabs>
          )}

          <div className="text-center py-2.5 text-[11px] text-muted-foreground/70 border-t flex-shrink-0 bg-muted/10">
            <a href="https://feednatic.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors inline-flex items-center gap-1.5 hover:underline">
              <span className="text-sm">⚡</span>
              <span>Powered by Feednatic</span>
            </a>
          </div>
        </Card>
      ) : (
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full h-16 w-16 p-0 shadow-2xl hover:shadow-3xl transition-all hover:scale-110 active:scale-95 border-2 border-white/20"
          size="icon"
          variant="default"
          style={primaryColor ? {
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, 30)} 100%)`,
            color: textColor
          } : {
            background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)) 100%)'
          }}
        >
          <MessageCircle className="h-7 w-7" />
        </Button>
      )}
    </div>
  );
};
